<template>
  <section class="task-page">
    <TabsSwitchBar v-model="section" :tabs="tabs" :role-class="roleClass" :mobile-equal="true" />

    <QueryErrorNotice :error="taskPageErrors" />

    <div class="section__task">
      <template v-if="section === 'task'">
        <div v-for="(entry, index) in localTasks" :key="`task-tab-${entry.id}-${index}`" class="mb-4">
          <span v-if="entry.theme" class="theme__name">{{ entry.theme }}</span>
          <TaskTasksTab
            :lesson-id="entry.id"
            :pupil-id="pupilId"
            :role-id="roleId"
            @loading-change="setTabLoading('task', $event)"
          />
        </div>
      </template>

      <template v-else-if="section === 'material'">
        <div v-for="(entry, index) in localTasks" :key="`material-tab-${entry.id}-${index}`" class="mb-4">
          <span v-if="entry.theme" class="theme__name">{{ entry.theme }}</span>
          <TaskMaterialsTab
            :lesson-id="entry.id"
            :pupil-id="pupilId"
            :role-id="roleId"
            @loading-change="setTabLoading('material', $event)"
          />
        </div>
      </template>

      <template v-else>
        <div v-for="(entry, index) in localTasks" :key="`test-tab-${entry.id}-${index}`" class="mb-4">
          <span v-if="entry.theme" class="theme__name">{{ entry.theme }}</span>
          <TaskTestsTab
            :lesson-id="entry.id"
            :pupil-id="pupilId"
            :role-id="roleId"
            @loading-change="setTabLoading('test', $event)"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import TabsSwitchBar from '@/components/common/TabsSwitchBar.vue';
import QueryErrorNotice from '@/components/common/QueryErrorNotice.vue';
import TaskMaterialsTab from '@/components/task/TaskMaterialsTab.vue';
import TaskTasksTab from '@/components/task/TaskTasksTab.vue';
import TaskTestsTab from '@/components/task/TaskTestsTab.vue';
import { useTaskSdotTokenMutation } from '@/queries/task/useTaskQueries';
import { getSessionServerId } from '@/services/session';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const viewerStore = useViewerStore();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();

viewerStore.initFromStorage();

const section = ref('task');
const tabLoading = ref({
  task: false,
  material: false,
  test: false,
});
const tokenLoading = ref(false);

const selectedChild = computed(() => viewerStore.selectedChild || null);
const roleId = computed(() => (viewerStore.isParent ? 2 : 3));
const roleClass = computed(() => (viewerStore.isParent ? 'active-parent' : 'active-pupil'));

const pupilId = computed(() =>
  Number(selectedChild.value?.pupil_id || viewerStore.profile?.pupil_id || 0)
);

clearStaleSdotToken();

const lessonId = computed(() => Number(route.query.lessonId || 0));
const lessonName = computed(() => String(route.query.lessonName || ''));
const lessonTheme = computed(() => String(route.query.theme || '').trim());
const dayTs = computed(() => Number(route.query.day || 0));

const localTasks = computed(() => {
  if (lessonId.value > 0) return [{ id: lessonId.value, theme: lessonTheme.value }];
  return [];
});

const tabs = computed(() => [
  { value: 'task', label: t('tasks') },
  { value: 'material', label: t('materials') },
  { value: 'test', label: t('tests') },
]);

const sdotTokenMutation = useTaskSdotTokenMutation();

const isLoading = computed(
  () =>
    tokenLoading.value ||
    tabLoading.value.task ||
    tabLoading.value.material ||
    tabLoading.value.test
);
const taskPageErrors = computed(() =>
  sdotTokenMutation.isError.value ? [sdotTokenMutation.error.value] : []
);

watch(
  () => isLoading.value,
  (value) => loadingStore.set('task-page', value),
  { immediate: true }
);

// watch(
//   () => [lessonName.value, dayTs.value],
//   () => {
//     topbarStore.setTitle(buildTaskTitle());
//   },
//   { immediate: true }
// );

onMounted(async () => {
  if (route.query.section && ['task', 'material', 'test'].includes(String(route.query.section))) {
    section.value = String(route.query.section);
  }
  topbarStore.setTitle(buildTaskTitle());
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => {
      if (dayTs.value > 0) {
        router.push(`/schedule?day=${dayTs.value}`);
      } else {
        router.push('/schedule');
      }
    },
  });

  await ensureSdotToken();
});

watch(
  () => pupilId.value,
  async (next, prev) => {
    if (!next || next === prev) return;
    clearSdotToken();
    await ensureSdotToken();
  }
);

onBeforeUnmount(() => {
  loadingStore.hide('task-page');
});

function setTabLoading(key, value) {
  tabLoading.value = {
    ...tabLoading.value,
    [key]: Boolean(value),
  };
}

function buildTaskTitle() {
  const base = lessonName.value || t('tasks');
  const ts = Number(dayTs.value || 0);
  if (!ts) return base;
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return base;
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${base} (${dd}.${mm})`;
}

function resolveServerId() {
  const fromSession = Number(getSessionServerId() || 0);
  if (fromSession > 0) return fromSession;

  let childListRaw = [];
  try {
    childListRaw = JSON.parse(localStorage.getItem('childList') || '[]');
  } catch {
    childListRaw = [];
  }
  const currentChildUin = String(localStorage.getItem('currentChildUin') || '');
  if (Array.isArray(childListRaw) && currentChildUin) {
    const child = childListRaw.find((item) => String(item?.uin || '') === currentChildUin);
    const childServerId = Number(child?.server_id || child?._data?.server_id || child?._server?.id || child?.server?.id || 0);
    if (childServerId > 0) return childServerId;
  }
  return 0;
}

async function ensureSdotToken() {
  if (Number(roleId.value || 3) !== 3) return;

  const serverId = resolveServerId();
  if (!serverId || !pupilId.value) return;

  tokenLoading.value = true;
  try {
    const response = await sdotTokenMutation.mutateAsync({
      serverId,
      pupilId: pupilId.value,
    });
    const token = String(response?.token || '');
    const organizationId = String(response?.organization_id || '');
    if (token) localStorage.setItem('sdotToken', token);
    if (organizationId) localStorage.setItem('organization_id', organizationId);
    localStorage.setItem('sdotPupilId', String(pupilId.value));
  } catch {
    // keep silent: page can still show local sdot_data
  } finally {
    tokenLoading.value = false;
  }
}

function clearSdotToken() {
  localStorage.removeItem('sdotToken');
  localStorage.removeItem('organization_id');
  localStorage.removeItem('sdotPupilId');
}

function clearStaleSdotToken() {
  const currentPupilId = Number(pupilId.value || 0);
  const tokenPupilId = Number(localStorage.getItem('sdotPupilId') || 0);
  const hasToken = Boolean(localStorage.getItem('sdotToken'));
  if (currentPupilId && hasToken && tokenPupilId !== currentPupilId) {
    clearSdotToken();
  }
}
</script>

<style scoped lang="scss">
.section__task {
  padding: 12px;
}

.theme__name {
  color: #333;
  display: flex;
  align-items: center;
  margin: 0 8px;
  font-size: 16px;
  font-weight: 700;
}

@media screen and (max-width: 767px) {
  .section__task {
    margin-top: 20px;
  }
}

@media screen and (min-width: 768px) {
  .section__task {
    margin-top: 50px;
  }
}
</style>
